import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { TPIIndicator, VehicleFacade } from '@amfam/policy/vehicle/data-access';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import {
  AnalyticsEventType,
  ReplaceVehicleQuickQuoteOperation
} from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from './../+state/quick-quote-feature/quick-quote-feature.facade';

@Component({
  selector: 'amfam-loan-info',
  templateUrl: './loan-info.component.html',
  styleUrls: ['./loan-info.component.scss'],
  standalone: false
})
export class LoanInfoComponent implements OnInit, OnDestroy {
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  tpiDescriptionList$ = this.quickQuoteFeatureFacade.getTpisInSelectedPolicy$.pipe(
    map(tpis => [
      ...tpis.map(t => ({ id: t.name, text: t.name })),
      {
        text: this.copyService.getCopy('policy.newVehicleTPINoneOfTheseText'),
        id: 'None'
      }
    ])
  );

  @Output() cancelEvent = new EventEmitter();

  public tpisInfoForm: UntypedFormGroup;
  private stop$: Subject<void> = new Subject<void>();
  public userState$ = this.store.select(userQuery.selectUserStateCode);
  public canShowDatePicker: boolean;
  public headerContentPath: string;
  exitFlowModalId = this.modalService.createModalId('exitFlowModalId');

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService,
    private spinnerService: LoadingSpinnerService,
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private vehicleFacade: VehicleFacade,
    private modalService: DsModalService,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService
  ) {}

  ngOnInit(): void {
    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Replace a Vehicle');
    this.buildForm();
    this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.REPLACE_VEHICLE_TPI_DETAILS);
  }

  private buildForm() {
    this.headerContentPath = 'policy.newVehicleLoanLeaseInstitutions';
    this.tpisInfoForm = this.formBuilder.group({
      tpiOption: ['', Validators.compose([Validators.required])]
    });
  }

  submitTPIsInfo() {
    this.spinnerService.start({
      secondaryMessage: `We're calculating your quote. This may take a moment.`,
      blockActions: true,
      maxWidth: '338'
    });

    if (this.tpisInfoForm.value.tpiOption.id !== 'None')
      this.vehicleFacade.setTpi(this.tpisInfoForm.value.tpiOption.id, TPIIndicator.YES);
    else this.vehicleFacade.setTpi('', TPIIndicator.NO);

    this.quickQuoteFeatureFacade.getQuote('quotes');
  }

  cancel() {
    this.modalService.open(this.exitFlowModalId);
    this.quickQuoteFeatureFacade.sendAnalytics(
      AnalyticsEventType.REPLACE_VEHICLE_TPI_DETAILS_CANCEL
    );
  }

  stay() {
    this.modalService.close(this.exitFlowModalId);
  }

  sendToAgent() {
    this.spinnerService.start({ blockActions: true });
    this.quickQuoteFeatureFacade.setOperation(
      ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_TPI_SEND_TO_AGENT
    );
    this.quickQuoteFeatureFacade.sendToAgentOnCancel();
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  navigateToOverview() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies']
      })
    );
  }

  ngOnDestroy() {
    this.spinnerService.stop();
    this.stop$.next();
    this.stop$.complete();
  }
}
